/**
 * @module AEM
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AppConfig } from './../../../root/app.config';
import { WorkerRequestArgs } from '@core/worker/models/worker-request-args';
import { CONTENT_PATH_CONSTANT } from '@support/shared/constants/device.constants';
import { WorkerService } from '@core/worker/services/worker.service';
import { map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
export enum PathState {
  Pending,
  Success,
  Failed,
}
@Injectable({
  providedIn: 'root',
})
export class ContentPathsService {
  private subject: Subject<Object>;
  private contentPaths: Object;

  constructor(
    private workerService: WorkerService,
    private appConfig: AppConfig
  ) {
    this.subject = new Subject<Object>();
    this.getContentPaths();
  }

  public getPath(key: string): Promise<string> {
    // window[SHARED_CONSTANTS.DIGITALDATA][SHARED_CONSTANTS.PAGE][SHARED_CONSTANTS.PAGEINFO][SHARED_CONSTANTS.URL] = window.location.href;
    const state: PathState = this.checkValid(key);
    return new Promise((resolve: Function, reject: Function) => {
      // Pending Request
      if (state === PathState.Pending) {
        this.subject.subscribe(
          (res: Object) => {
            const state2: PathState = this.checkValid(key);
            // Failed Request
            if (state2 === PathState.Failed) {
              return reject('Failed to get Content Path');
            }

            if (state2 === PathState.Success) {
              return resolve(res[key]);
            }

            if (state2 === PathState.Pending) {
              throw new Error('Content Path cannot be pending');
            }
          },
          (err: Error) => {
            return reject(`Failed to get Content Path: ${err}`);
          }
        );
      }

      // Failed Request
      if (state === PathState.Failed) {
        return reject('Failed to get Content Path');
      }

      if (state === PathState.Success) {
        return resolve(this.contentPaths[key]);
      }
    });
  }

  private checkValid(key: string): PathState {
    const val: Object = this.contentPaths;
    // If empty object then the request failed
    if (this.isEmpty(val)) {
      return PathState.Failed;
    }

    if (val && val[key]) {
      // If the object exists and has the key then return success
      return PathState.Success;
    }

    if (val) {
      return val[key] ? PathState.Success : PathState.Failed;
    } else {
      return PathState.Pending;
    }
  }

  private getContentPaths(): void {
    const domain: string = this.appConfig.getConfig('aemEndPoint');
    const aemVersion = this.appConfig.getConfig('aemContentVersion')
      ? this.appConfig.getConfig('aemContentVersion')
      : '1.0';

    const workerArg: WorkerRequestArgs = {
      headers: null,
      preferCache: false,
      cacheResponse: false,
      cacheKey: `${domain}` + CONTENT_PATH_CONSTANT.CONTENT_PATHS.concat(aemVersion),
    };
    this.workerService
      .get(`${domain}` + CONTENT_PATH_CONSTANT.CONTENT_PATHS, workerArg)
      .pipe(
        map((res: HttpResponse<Object>) => {
          return res.body;
        })
      )
      .subscribe(
        (res: Object) => {
          this.contentPaths = res;
          this.subject.next(res);
        },
        (err: Error) => {
          this.contentPaths = {};
          this.subject.next({});
          this.subject.error(err);
        }
      );
  }

  private isEmpty(obj: Object): boolean {
    for (const prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false;
      }
    }
    return JSON.stringify(obj) === JSON.stringify({});
  }
}
